import type { ScoutQuestionCommand } from "providers/ChatSurfaceCoordinator";

import { formatSkillPickContext } from "encamp-shared/src/skills/skillPickContext";
import { ScoutHost } from "generated-graphql/graphql";
import { useCallback } from "react";

import type { useScoutConversation } from "../hooks/useScoutConversation";

import { type ScoutSubmitHandler } from "../Chat/ScoutInput";
import { mergeScoutPageContext } from "../Chat/skills/selectedSkillContext";
import { SCOUT_WRITE_MODE_GQL, type ScoutWriteModeGql } from "../hooks/scoutWriteMode";
import { buildPopoverPageContext } from "./popoverPageContext";

type SendMessage = ReturnType<typeof useScoutConversation>["sendMessage"];

export function usePopoverScoutMessageSender({
  pathname,
  search,
  openedAt,
  hasScoutProAccess,
  writeMode,
  sendMessage,
}: {
  pathname: string;
  search: string;
  openedAt: Date;
  hasScoutProAccess: boolean;
  writeMode: ScoutWriteModeGql;
  sendMessage: SendMessage;
}): {
  handleSubmit: ScoutSubmitHandler;
  askQuestionCommand: (command: ScoutQuestionCommand) => void;
} {
  const sendPopoverMessage = useCallback(
    async (
      message: Parameters<ScoutSubmitHandler>[0],
      options: Parameters<ScoutSubmitHandler>[1],
      popoverOptions: { requestContext?: string; startNewConversation?: boolean }
    ) => {
      const pageContext = mergeScoutPageContext(
        buildPopoverPageContext({
          pathname,
          search,
          pageTitle: typeof document !== "undefined" ? document.title : "",
          openedAt,
          requestContext: popoverOptions.requestContext,
        }),
        formatSkillPickContext(options?.skillPicks ?? [])
      );
      const attachments = options?.attachments;
      await sendMessage(message, {
        ...(popoverOptions.startNewConversation ? { conversationId: null } : {}),
        ...(pageContext ? { pageContext } : {}),
        host: ScoutHost.Popover,
        writeMode: hasScoutProAccess ? writeMode : SCOUT_WRITE_MODE_GQL.OFF,
        attachmentIds: hasScoutProAccess
          ? attachments?.map((attachment) => attachment.privateDocumentId)
          : undefined,
        attachmentPreviews: hasScoutProAccess
          ? attachments?.map((attachment) => ({
              privateDocumentId: attachment.privateDocumentId,
              filename: attachment.filename,
              mediaType: attachment.mediaType,
              sizeBytes: attachment.sizeBytes,
              signedDownloadUrl: attachment.signedDownloadUrl,
              thumbnailSignedUrl: attachment.thumbnailSignedUrl,
              pageCount: attachment.pageCount,
            }))
          : undefined,
        extractionWatchPrivateDocumentIds: options?.extractionWatchPrivateDocumentIds,
      });
    },
    [hasScoutProAccess, openedAt, pathname, search, sendMessage, writeMode]
  );

  const handleSubmit = useCallback<ScoutSubmitHandler>(
    async (message, options) => sendPopoverMessage(message, options, {}),
    [sendPopoverMessage]
  );

  const askQuestionCommand = useCallback(
    (command: ScoutQuestionCommand) => {
      // Fired from an effect, so a rejection here has nowhere to surface — the
      // controller already renders send failures in the thread itself.
      void sendPopoverMessage(command.question, undefined, {
        ...(command.context ? { requestContext: command.context } : {}),
        startNewConversation: true,
      }).catch(() => undefined);
    },
    [sendPopoverMessage]
  );

  return { handleSubmit, askQuestionCommand };
}
